import { useMemo } from "react";
import { Box, FormControl, ListItemText, MenuItem, Select, Typography } from "@mui/material";
import { ApiEndpoint } from "../../../types";
import {
  baseGeometry,
  isIconStyled,
  getRowStyleValue,
  getStyleChoices,
  getStyleFieldKey,
  buildRowMapDisplayConfig,
  CmStyleChoice
} from "../../../utilities/cmTableStyle";
import { resolveIconUrl } from "../../../utilities/cdnUtils";

// Re-exported for existing importers (e.g. EntityForm). `resolveIconUrl` now lives
// in cdnUtils so it also resolves shared `public-icons/…` refs to signed CDN URLs.
export { resolveIconUrl };

interface SchemaField {
  name: string;
  type?: string;
  label?: string;
  choices?: string[];
}

interface CmTableRowDataFieldsProps {
  endpoint: ApiEndpoint;
  /** The row's own `map_display_config` (per-row style override). */
  mapDisplayConfig: Record<string, any>;
  /** Called with the next `map_display_config` when the user picks/clears a style. */
  onMapDisplayConfigChange: (next: Record<string, any>) => void;
  disabled?: boolean;
}

/** Small swatch shown beside a style choice — icon thumbnail (point) or color dot. */
function ChoiceSwatch({ choice, iconStyled }: { choice: CmStyleChoice; iconStyled: boolean }) {
  if (iconStyled) {
    return (
      <Box component='img' src={resolveIconUrl(choice.icon)} alt='' sx={{ width: 20, height: 20, objectFit: "contain", flexShrink: 0 }} />
    );
  }
  return <Box sx={{ width: 16, height: 16, borderRadius: "3px", bgcolor: choice.color || "#9ca3af", flexShrink: 0 }} />;
}

/**
 * Per-row map styling control for a CM-table row.
 *
 * The table config's style `match` expression defines the named choices
 * (e.g. Camp, Ktms, Tset) and their icon/colour. The user picks one of those
 * named types from a dropdown; the chosen value (e.g. "camp") is stored on the
 * row's own `map_display_config` under the styling field key
 * (`{ type: "camp" }`) — NOT inside `row_data`. The table-level style resolves
 * that value to the row's icon/colour on the map. Clearing the selection falls
 * back to the table-level default styling. The value is sent with the row's
 * create/update payload — no separate table PATCH.
 */
export default function CmTableRowDataFields({
  endpoint,
  mapDisplayConfig,
  onMapDisplayConfigChange,
  disabled
}: CmTableRowDataFieldsProps) {
  const spatialType = endpoint.spatialType;
  const style = endpoint.style;
  const iconStyled = isIconStyled(spatialType);
  const geometrySupportsStyling = baseGeometry(spatialType) !== "none";

  const styleFieldKey = useMemo(() => endpoint.styleKey || getStyleFieldKey(style, spatialType), [endpoint.styleKey, style, spatialType]);
  const styleChoices = useMemo(() => getStyleChoices(style, spatialType), [style, spatialType]);

  const schemaFields = useMemo<SchemaField[]>(() => (endpoint.fields as SchemaField[]) ?? [], [endpoint.fields]);

  // The human-readable name of the styling field — from its field_schema `label`,
  // falling back to the style key — so each table shows its own term (Type, Category…).
  const styleLabel = useMemo(() => {
    const fromSchema = schemaFields.find((f) => f.name === styleFieldKey)?.label;
    const base = (fromSchema && fromSchema.trim()) || styleFieldKey || "Type";
    return base.charAt(0).toUpperCase() + base.slice(1);
  }, [schemaFields, styleFieldKey]);

  const selectedValue = useMemo(() => getRowStyleValue(mapDisplayConfig, styleFieldKey) ?? "", [mapDisplayConfig, styleFieldKey]);

  if (!geometrySupportsStyling || styleChoices.length === 0) return null;

  const handleSelect = (value: string) => {
    onMapDisplayConfigChange(buildRowMapDisplayConfig(styleFieldKey, value || null));
  };

  return (
    <FormControl fullWidth sx={{ gap: "0.37rem" }}>
      <Typography variant='body1' color='grey.500'>
        {styleLabel}
      </Typography>
      <Select
        value={selectedValue}
        displayEmpty
        disabled={disabled}
        onChange={(e) => handleSelect(e.target.value)}
        renderValue={(selected) => {
          if (!selected) return <Typography color='grey.400'>Default styling</Typography>;
          const choice = styleChoices.find((c) => c.value === selected);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {choice && <ChoiceSwatch choice={choice} iconStyled={iconStyled} />}
              <span>{String(selected)}</span>
            </Box>
          );
        }}
        sx={{ "& fieldset": { borderColor: "grey.300", borderRadius: "0.5rem" } }}
      >
        <MenuItem value=''>
          <Typography color='grey.500'>Default styling</Typography>
        </MenuItem>
        {styleChoices.map((choice) => (
          <MenuItem key={choice.value} value={choice.value} sx={{ gap: "0.5rem" }}>
            <ChoiceSwatch choice={choice} iconStyled={iconStyled} />
            <ListItemText primary={choice.value} />
          </MenuItem>
        ))}
      </Select>
      <Typography variant='caption' color='grey.400'>
        {selectedValue ? "This record uses its own map styling." : "This record uses the table's default styling."}
      </Typography>
    </FormControl>
  );
}
